<script lang="ts">
	import { setContext } from "svelte";
	import Layout from "../components/Layout.svelte";
	import Link from "../components/Link.svelte";
	import Card from "../components/Card.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import HotlineGetInTouch from "../components/HotlineGetInTouch.svelte";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import { getTenant } from "../getTenant.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap.js";
	import { caseStudiesPathMap } from "../../../core/schema/paths/caseStudiesPathMap.js";
	import { getReferenceDetailPath } from "../../../core/schema/paths/getReferenceDetailPath.js";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import translations from "./ServiceSupportLifecycle.translations.json";
	import type { ServiceSupportLifecycleProps } from "./ServiceSupportLifecycleProps.js";
	// Not shown on the page, only used as the page thumbnail (social preview).
	import thumbnailImage from "../assets/images/services/service/4.jpg";
	import wideImage1Sm from "../assets/images/services/service/3.jpg?webp&h=320&w=768&imagetools";
	import wideImage1Md from "../assets/images/services/service/3.jpg?webp&h=380&w=1024&imagetools";
	import wideImage1Lg from "../assets/images/services/service/3.jpg?webp&h=460&w=1440&imagetools";
	import wideImage1Xl from "../assets/images/services/service/3.jpg?webp&h=560&w=1920&imagetools";
	import wideImage2Sm from "../assets/images/services/service/8.jpg?webp&h=320&w=768&imagetools";
	import wideImage2Md from "../assets/images/services/service/8.jpg?webp&h=380&w=1024&imagetools";
	import wideImage2Lg from "../assets/images/services/service/8.jpg?webp&h=460&w=1440&imagetools";
	import wideImage2Xl from "../assets/images/services/service/8.jpg?webp&h=560&w=1920&imagetools";
	import wideImage3Sm from "../assets/images/services/service/5.jpg?webp&h=320&w=768&imagetools";
	import wideImage3Md from "../assets/images/services/service/5.jpg?webp&h=380&w=1024&imagetools";
	import wideImage3Lg from "../assets/images/services/service/5.jpg?webp&h=460&w=1440&imagetools";
	import wideImage3Xl from "../assets/images/services/service/5.jpg?webp&h=560&w=1920&imagetools";

	type $$Props = ServiceSupportLifecycleProps;

	const tenant = getTenant();
	const { relatedCaseStudies } = $$restProps as $$Props;
	const translate = getTranslate(translations);

	setContext(thumbnailImageContextKey, thumbnailImage);

	interface Keypoint {
		label: string;
		text: string;
	}

	interface WideImage {
		sm: string;
		md: string;
		lg: string;
		xl: string;
	}

	interface Section {
		heading: string;
		keypoints: Keypoint[];
	}

	/** One wide photo sits between every two sections, so there is one fewer than sections. */
	const wideImages: WideImage[] = [
		{ sm: wideImage1Sm, md: wideImage1Md, lg: wideImage1Lg, xl: wideImage1Xl },
		{ sm: wideImage2Sm, md: wideImage2Md, lg: wideImage2Lg, xl: wideImage2Xl },
		{ sm: wideImage3Sm, md: wideImage3Md, lg: wideImage3Lg, xl: wideImage3Xl },
	];

	const sections: Section[] = [
		{
			heading: translate("section1Heading"),
			keypoints: [
				{ label: translate("section1Keypoint1Label"), text: translate("section1Keypoint1Text") },
				{ label: translate("section1Keypoint2Label"), text: translate("section1Keypoint2Text") },
				{ label: translate("section1Keypoint3Label"), text: translate("section1Keypoint3Text") },
				{ label: translate("section1Keypoint4Label"), text: translate("section1Keypoint4Text") },
			],
		},
		{
			heading: translate("section2Heading"),
			keypoints: [
				{ label: translate("section2Keypoint1Label"), text: translate("section2Keypoint1Text") },
				{ label: translate("section2Keypoint2Label"), text: translate("section2Keypoint2Text") },
			],
		},
		{
			heading: translate("section3Heading"),
			keypoints: [
				{ label: translate("section3Keypoint1Label"), text: translate("section3Keypoint1Text") },
				{ label: translate("section3Keypoint2Label"), text: translate("section3Keypoint2Text") },
				{ label: translate("section3Keypoint3Label"), text: translate("section3Keypoint3Text") },
				{ label: translate("section3Keypoint4Label"), text: translate("section3Keypoint4Text") },
				{ label: translate("section3Keypoint5Label"), text: translate("section3Keypoint5Text") },
			],
		},
		{
			heading: translate("section4Heading"),
			keypoints: [
				{ label: translate("section4Keypoint1Label"), text: translate("section4Keypoint1Text") },
				{ label: translate("section4Keypoint2Label"), text: translate("section4Keypoint2Text") },
				{ label: translate("section4Keypoint3Label"), text: translate("section4Keypoint3Text") },
				{ label: translate("section4Keypoint4Label"), text: translate("section4Keypoint4Text") },
			],
		},
	];
</script>

<Layout>
	<article>
		<section>
			<div
				class="border-lttertiary border-b px-4 pb-12 pt-6 sm:px-8 md:px-16 md:pb-20 md:pt-10 lg:pb-24 lg:pt-12 xl:px-32"
			>
				<div
					class="text-ltprimary-200 mb-8 flex items-center gap-4 text-xs uppercase tracking-[0.15em] md:mb-12 lg:mb-16"
				>
					<span class="bg-ltprimary-200 h-px w-8"></span>
					<span>{translate("tag")}</span>
				</div>

				<div class="grid grid-cols-1 gap-6 md:gap-10 lg:grid-cols-2 lg:gap-16 xl:gap-32">
					<h1
						class="text-ltprimary sm:text-3lg m-0 p-0 text-[2.25rem]/[1.1] font-light uppercase tracking-[0.02em] md:text-2xl lg:text-2xl xl:text-3xl"
					>
						{nbspify(translate("headline"))}
					</h1>
					<div class="text-ltsecondary flex flex-col gap-6 md:gap-8">
						<p class="text-sm leading-[1.6] tracking-[0.01em] md:text-base">
							{nbspify(translate("introParagraph"))}
						</p>
						<div>
							<Link asButton variant="primary" icon={arrowRight} href="/{contactPathMap[tenant.locale]}">
								{translate("contactUs")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>

		{#each sections as section, sectionIndex}
			<section class="container">
				<div
					class="mx-auto flex max-w-[52rem] flex-col gap-5 px-4 pb-12 sm:px-8 md:gap-6 md:py-16 lg:pb-24 {sectionIndex ===
					0
						? 'pt-12 lg:pt-24'
						: ''}"
				>
					<h2
						class="text-ltprimary font-roboto text-2lg/[1.1] m-0 font-light uppercase tracking-[0.02em] md:text-[2.5rem]/none"
					>
						{section.heading}
					</h2>

					<div class="bg-lttertiary-100 mt-2 flex flex-col overflow-hidden rounded-md md:mt-4">
						{#each section.keypoints as keypoint, index}
							<div
								class="grid grid-cols-1 gap-3 p-5 sm:p-6 md:grid-cols-[minmax(0,14rem)_1fr] md:gap-8 md:p-8 {index <
								section.keypoints.length - 1
									? 'border-b-lttertiary border-b'
									: ''}"
							>
								<div
									class="text-ltprimary-400 font-roboto text-md/tighter md:text-2md/none font-normal uppercase tracking-[0.02em]"
								>
									{keypoint.label}
								</div>
								<div class="text-ltsecondary text-sm leading-[1.6] md:text-base/normal">
									{nbspify(keypoint.text)}
								</div>
							</div>
						{/each}
					</div>
				</div>
			</section>

			{#if wideImages[sectionIndex] !== undefined}
				{@const wideImage = wideImages[sectionIndex]}
				<div class="w-full">
					<StaticPicture
						image={wideImage.xl}
						alt="{translate('headline')} - {translate('ilustration')}"
						width={1920}
						height={560}
						loading="lazy"
						imgClass="h-[13.75rem] w-full object-cover sm:h-80 md:h-[25rem] lg:h-[30rem] xl:h-[35rem]"
						class="mb-12 w-full md:mb-16 lg:mb-24"
						sourcesets={{
							"<md": wideImage.sm,
							">md": wideImage.md,
							">lg": wideImage.lg,
							">xl": wideImage.xl,
						}}
					/>
				</div>
			{/if}
		{/each}

		<HotlineGetInTouch variant="bordered" />

		{#if relatedCaseStudies.length > 0}
			<section class="container">
				<div class="flex flex-col items-center gap-8 px-4 py-12 sm:px-8 md:gap-10 md:p-16 lg:py-24 xl:px-32">
					<h2
						class="font-bahnschrift text-ltprimary text-md text-center font-light uppercase tracking-wider md:text-lg lg:text-xl"
					>
						{translate("caseStudiesHeadline")}
					</h2>
					<div
						class="grid w-full gap-4 {relatedCaseStudies.length < 3
							? 'grid-cols-1 sm:grid-cols-2'
							: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}"
					>
						{#each relatedCaseStudies as caseStudy}
							<Card
								title={caseStudy.title}
								description={caseStudy.perex}
								urlSlug={getReferenceDetailPath(caseStudy)}
								image={caseStudy.previewImage}
								squaredImage={false}
							/>
						{/each}
					</div>
					<div class="mt-2 md:mt-4">
						<Link asButton variant="secondary" icon={arrowRight} href="/{caseStudiesPathMap[tenant.locale]}">
							{translate("caseStudiesButton")}
						</Link>
					</div>
				</div>
			</section>
		{/if}

		<section class="!mb-0">
			<ContactForm containerClass="mx-4 md:px-8 lg:px-12 xl:px-28" />
		</section>
	</article>
</Layout>
